import { memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FOR_INVESTORS_SECTIONS, FOR_INVESTORS_SUB_SECTIONS } from 'constants/businessToolSections/forInvestors';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import styles from 'components/BusinessToolForm/Sections/MarketAnalysis/CompetitiveAnalysis/CompetitiveAnalysis.module.scss';
import { CompetitorComparison } from 'components/BusinessToolForm/Sections/MarketAnalysis/CompetitiveAnalysis/CompetitorComparison';
import { CompetitorsQualities } from 'components/BusinessToolForm/Sections/MarketAnalysis/CompetitiveAnalysis/CompetitorsQualities';
import { KeyCompetitors } from 'components/BusinessToolForm/Sections/MarketAnalysis/CompetitiveAnalysis/KeyCompetitors';
import {
  companyQualitiesValues,
  COMPETITIVE_ANALYSIS_NAMES,
  competitorsComparisonValues,
  keyCompetitorsValues,
  prefix,
} from 'components/BusinessToolForm/Sections/MarketAnalysis/CompetitiveAnalysis/utils/form';

import { concatWithDot } from 'helpers/string';

export const CompetitiveAnalysisMarket: React.FC = memo(() => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_INVESTORS_SECTIONS.MARKET_ANALYSIS,
    FOR_INVESTORS_SUB_SECTIONS.FOR_INVESTORS_MARKET_ANALYSIS.COMPETITIVE_ANALYSIS,
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('marketAnalysisForm.title')}
          badgeText={t('marketAnalysisForm.competitors.title')}
          section={FOR_INVESTORS_SECTIONS.MARKET_ANALYSIS}
        />
      </FormSectionCard>
      {/* 4.2.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.competitors.directCompetitors')}
        aiFieldName={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.COMPETITORS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.COMPETITORS)}
          name={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.COMPETITORS)}
          minRows={3}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.2.3 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.competitors.keyCompetitors.title')}
        aiFieldName={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.KEY_COMPETITORS)}
        aiFieldType="complex"
        collapsible
      >
        <FieldArrayComponent
          name={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.KEY_COMPETITORS)}
          initialItem={keyCompetitorsValues}
          buttonText={t('marketAnalysisForm.competitors.qualitiesButtonText')}
          disabled={!canAccess}
        >
          {(namePrefix) => <KeyCompetitors name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 4.2.4 */}
      <FormSectionCard
        collapsible
        disabled={!canAccess}
        title={t('marketAnalysisForm.competitors.qualities.title')}
        aiFieldName={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_QUALITIES)}
        aiFieldType="complex"
      >
        <CompetitorsQualities
          name={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.YOUR_COMPANY_QUALITIES)}
          disabled={!canAccess}
          title={t('marketAnalysisForm.competitors.yourCompanyName')}
          className={styles.competitorQualities}
          isOurCompany
        />
        <FieldArrayComponent
          maxLength={4}
          name={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_QUALITIES)}
          initialItem={companyQualitiesValues}
          buttonText={t('marketAnalysisForm.competitors.qualitiesButtonText')}
          disabled={!canAccess}
        >
          {(namePrefix) => (
            <CompetitorsQualities
              name={namePrefix}
              disabled={!canAccess}
              title={t('marketAnalysisForm.competitors.qualitiesButtonText')}
            />
          )}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 4.2.7 */}
      <FormSectionCard disabled={!canAccess} title={t('marketAnalysisForm.competitors.productsCompare')} collapsible>
        <FieldArrayComponent
          name={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.COMPARISON_ITEM)}
          buttonText={t('marketAnalysisForm.competitors.productsCompareButtonText')}
          initialItem={competitorsComparisonValues}
          disabled={!canAccess}
        >
          {(namePrefix) => <CompetitorComparison name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 4.2.9 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.competitors.barriers')}
        aiFieldName={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.BARRIERS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.BARRIERS)}
          name={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.BARRIERS)}
          minRows={3}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
    </>
  );
});
